import type { AuthUser } from "@dentacore/shared-types";

import { readSetupState } from "../api/setup";

/**
 * Where a successful sign-in lands — Home, or guided setup (decision G3).
 *
 * **This is the "first sign-in" redirect, and it lives here rather than on a
 * route on purpose.** A gate on the Home route would fire on every visit, which
 * would make Home unreachable for an incomplete clinic and contradict the
 * story's own *leave and come back*. Signing in is the one moment where "take
 * me to what I still have to do" is the right answer; after that `/setup` is an
 * ordinary route that the Home banner links back to.
 *
 * **Administrators only** (S3). Nobody else may reach `/setup`, and nobody else
 * could act on what it says — a clinician signing into an empty clinic goes to
 * Home, exactly as before. Checking first also means non-admins never make the
 * call, rather than making it and being refused.
 *
 * **It asks `canAdminister`, not `role`** (2026-09-07). Administrative reach and
 * clinical identity became separate facts when a one-dentist practice needed
 * both, and this line was missed at the time: the owner of such a practice holds
 * `DENTIST` and `true`, so reading the role sent them to Home and they were
 * never offered the setup they are the only person able to finish.
 *
 * **A failure here is not a failed sign-in.** They are signed in perfectly
 * well, so an unreachable or unhappy server sends them to Home rather than
 * turning a working login into an error.
 */
export async function landing(user: AuthUser): Promise<string> {
  if (user.canAdminister !== true) return "/";

  try {
    const setup = await readSetupState();
    return setup.ready ? "/" : "/setup";
  } catch {
    return "/";
  }
}
